/**
 * Servicio de Autenticación — Cognito (AWS Amplify)
 * Reemplaza Appwrite SDK con Amplify Auth.
 */

import '../config/amplify'; // Inicializa Amplify
import { signIn, signOut, confirmSignIn, getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { ROLES } from '../constants/config';

class AuthService {
  /**
   * Obtiene la sesión actual del usuario
   * @returns {Promise<Object|null>} Usuario actual o null
   */
  async getCurrentSession() {
    try {
      const cognitoUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      const groups = session.tokens?.idToken?.payload?.['cognito:groups'] || [];
      return {
        $id: cognitoUser.userId,
        name: attributes.name || '',
        email: attributes.email || '',
        groups,
      };
    } catch (error) {
      console.log('No hay sesión activa');
      return null;
    }
  }

  /**
   * Verifica si un usuario tiene rol de administrador
   * @param {Object} user - Usuario a verificar
   * @returns {boolean} true si es admin
   */
  verifyAdminStatus(user) {
    if (!user) return false;
    const groups = user.groups || [];
    return groups.includes(ROLES.ADMIN) || groups.includes(ROLES.RESELLER);
  }

  isReseller(user) {
    if (!user) return false;
    const groups = user.groups || [];
    return groups.includes(ROLES.RESELLER) && !groups.includes(ROLES.ADMIN);
  }

  /**
   * Inicia sesión con email y contraseña
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña
   * @returns {Promise<{success: boolean, user?: Object, error?: string}>}
   */
  async login(email, password) {
    try {
      if (!email || !password) {
        return { success: false, error: 'Email y contraseña son requeridos' };
      }

      const { nextStep } = await signIn({ username: email, password });

      // Handle forced password change
      if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { success: false, needsNewPassword: true };
      }

      const userData = await this.getCurrentSession();

      const isAdmin = this.verifyAdminStatus(userData);
      if (!isAdmin) {
        await this.logout();
        return { success: false, error: 'No tienes permisos de administrador' };
      }

      return { success: true, user: userData };
    } catch (error) {
      console.error('Error en login:', error);
      return { success: false, error: error.message || 'Error al iniciar sesión' };
    }
  }

  /**
   * Completa el cambio de contraseña forzado
   */
  async completeNewPassword(newPassword) {
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      const userData = await this.getCurrentSession();

      const isAdmin = this.verifyAdminStatus(userData);
      if (!isAdmin) {
        await this.logout();
        return { success: false, error: 'No tienes permisos de administrador' };
      }

      return { success: true, user: userData };
    } catch (error) {
      console.error('Error cambiando contraseña:', error);
      return { success: false, error: error.message || 'Error al cambiar contraseña' };
    }
  }

  /**
   * Cierra la sesión actual
   */
  async logout() {
    try {
      await signOut();
      return { success: true };
    } catch (error) {
      console.error('Error en logout:', error);
      return { success: false, error: error.message || 'Error al cerrar sesión' };
    }
  }

  /**
   * Verifica si hay una sesión válida de administrador
   * @returns {Promise<{isValid: boolean, user?: Object}>}
   */
  async checkAdminSession() {
    try {
      const session = await this.getCurrentSession();
      if (!session) return { isValid: false };

      const isAdmin = this.verifyAdminStatus(session);
      if (!isAdmin) {
        await this.logout();
        return { isValid: false };
      }

      return { isValid: true, user: session };
    } catch (error) {
      console.error('Error verificando sesión:', error);
      return { isValid: false };
    }
  }
}

// Exportar como singleton
export default new AuthService();
